import { apiClient } from '../api/client';
import { API_PATHS } from '../api/endpoints';
import { mapTimestamps } from '../api/mapTimestamps';
import type { ApiListResponse, ApiItemResponse } from '../types/apiResponses';
import type { ManagedUser, CreateUserInput, InviteUserInput, UpdateUserInput } from '../types/user';

type BackendUser = Omit<ManagedUser, 'createdAt' | 'updatedAt' | 'companyId'> & {
  companyId?: string;
  clientId?: string;
  created: string;
  lastModified?: string;
};

function normalizeUser(user: BackendUser): ManagedUser {
  const mapped = mapTimestamps(user);
  const { clientId: _legacyCompanyId, ...mappedUser } = mapped;
  const disabled = mapped.disabled ?? mapped.status === 'disabled';

  return {
    ...mappedUser,
    username: mapped.username ?? mapped.email,
    name: mapped.name ?? mapped.email,
    role: mapped.role ?? 'facilitator',
    companyId: mapped.companyId ?? _legacyCompanyId ?? '',
    status: disabled ? 'disabled' : mapped.status ?? 'active',
    disabled,
  };
}

export async function getUsers(): Promise<ManagedUser[]> {
  const response = await apiClient.get<ApiListResponse<BackendUser>>(
    `${API_PATHS.users}?_=${Date.now()}`,
  );
  return response.items.map(normalizeUser);
}

export async function getUserById(id: string): Promise<ManagedUser> {
  const response = await apiClient.get<ApiItemResponse<BackendUser>>(`${API_PATHS.users}${id}`);
  return normalizeUser(response.item);
}

export async function getUsersByClient(companyId: string): Promise<ManagedUser[]> {
  const response = await apiClient.get<ApiListResponse<BackendUser>>(
    `${API_PATHS.users}?companyId=${encodeURIComponent(companyId)}&_=${Date.now()}`,
  );
  return response.items.map(normalizeUser).filter((user) => user.companyId === companyId);
}

export async function createUser(input: CreateUserInput): Promise<ManagedUser> {
  const response = await apiClient.post<ApiItemResponse<BackendUser>>(API_PATHS.users, input);
  return normalizeUser(response.item);
}

export async function inviteUser(input: InviteUserInput): Promise<ManagedUser> {
  const response = await apiClient.post<ApiItemResponse<BackendUser>>(API_PATHS.users, input);
  return normalizeUser(response.item);
}

export async function updateUser(id: string, input: UpdateUserInput): Promise<ManagedUser> {
  const response = await apiClient.patch<ApiItemResponse<BackendUser>>(`${API_PATHS.users}${id}`, input);
  return normalizeUser(response.item);
}

export async function disableUser(id: string): Promise<ManagedUser> {
  return updateUser(id, { disabled: true });
}

export async function deleteUser(id: string): Promise<void> {
  await apiClient.delete(`${API_PATHS.users}${id}`);
}
